// pages/stock/StockPage.js
import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';

import { useStockInfo } from '../../hooks/useStockInfo';
import { useColorModeValue } from '../../components/ui/color-mode';
import NotFound from '../NotFound';
import StockReviewModeControl from './StockReviewModeControl';
import { StockReviewModeProvider } from './StockReviewModeContext';

function StockPage() {
  const { ticker: rawTicker } = useParams();
  const location = useLocation();
  const normalizedTicker = rawTicker ? rawTicker.toUpperCase() : '';
  const lowerCaseTicker = rawTicker ? rawTicker.toLowerCase() : '';

  const { data: stockInfo, isNotFound } = useStockInfo(normalizedTicker);

  const seoMetadata = useMemo(() => {
    if (isNotFound) {
      return null;
    }

    if (!normalizedTicker) {
      return null;
    }

    const companyName = stockInfo?.company_name?.trim();
    const displayName = companyName
      ? `${companyName} (${normalizedTicker})`
      : normalizedTicker;

    const canonicalUrl = `https://dcfteacher.com/stock/${lowerCaseTicker}`;

    return {
      title: `${displayName} — DCF Valuation & Financials | DCF Teacher`,
      description: `DCF Teacher’s valuation dashboard for ${displayName}. Includes SEC filings, financial statements, insider trades, and DCF insights.`,
      canonicalUrl,
      ogTitle: `${displayName} — DCF Teacher`,
      ogDescription: `DCF valuation, financials, insider data, and filings for ${displayName}.`,
      ogUrl: canonicalUrl,
      robots: 'index, follow',
      ogType: 'website',
    };
  }, [normalizedTicker, lowerCaseTicker, stockInfo, isNotFound]);

  const originalTitleRef = useRef(null);
  const managedMetaTagsRef = useRef(new Map());
  const managedLinkTagsRef = useRef(new Map());

  useEffect(() => {
    if (!seoMetadata) {
      return;
    }

    if (originalTitleRef.current === null) {
      originalTitleRef.current = document.title;
    }

    const { title, description, canonicalUrl, ogTitle, ogDescription, ogUrl, robots, ogType } = seoMetadata;

    document.title = title;

    const updateMetaTag = (attribute, value, content) => {
      if (!content) return;
      const key = `${attribute}:${value}`;
      let tagEntry = managedMetaTagsRef.current.get(key);
      let tag = tagEntry?.element;

      if (!tag) {
        tag = document.head.querySelector(`meta[${attribute}="${value}"]`);
        const existed = Boolean(tag);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(attribute, value);
          document.head.appendChild(tag);
        }

        managedMetaTagsRef.current.set(key, {
          element: tag,
          initialContent: existed ? tag.getAttribute('content') : null,
          created: !existed,
        });
      }

      tag.setAttribute('content', content);
    };

    const updateLinkTag = (relValue, href) => {
      if (!href) return;
      let tagEntry = managedLinkTagsRef.current.get(relValue);
      let link = tagEntry?.element;

      if (!link) {
        link = document.head.querySelector(`link[rel="${relValue}"]`);
        const existed = Boolean(link);
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', relValue);
          document.head.appendChild(link);
        }

        managedLinkTagsRef.current.set(relValue, {
          element: link,
          initialHref: existed ? link.getAttribute('href') : null,
          created: !existed,
        });
      }

      link.setAttribute('href', href);
    };

    updateMetaTag('name', 'description', description);
    updateMetaTag('name', 'robots', robots);
    updateMetaTag('property', 'og:title', ogTitle);
    updateMetaTag('property', 'og:description', ogDescription);
    updateMetaTag('property', 'og:url', ogUrl);
    updateMetaTag('property', 'og:type', ogType);
    updateLinkTag('canonical', canonicalUrl);
  }, [seoMetadata]);

  useEffect(() => {
    const originalTitle = originalTitleRef.current;
    const managedMetaTags = managedMetaTagsRef.current;
    const managedLinkTags = managedLinkTagsRef.current;

    return () => {
      if (originalTitle !== null) {
        document.title = originalTitle;
      }

      managedMetaTags.forEach(({ element, initialContent, created }) => {
        if (created) {
          element.remove();
        } else if (initialContent === null || typeof initialContent === 'undefined') {
          element.removeAttribute('content');
        } else {
          element.setAttribute('content', initialContent);
        }
      });
      managedMetaTags.clear();

      managedLinkTags.forEach(({ element, initialHref, created }) => {
        if (created) {
          element.remove();
        } else if (initialHref === null || typeof initialHref === 'undefined') {
          element.removeAttribute('href');
        } else {
          element.setAttribute('href', initialHref);
        }
      });
      managedLinkTags.clear();
    };
  }, []);

  const mobileTabs = useMemo(() => {
    if (!lowerCaseTicker) {
      return [];
    }

    return [
      { label: 'Overview', to: `/stock/${lowerCaseTicker}`, exact: true },
      { label: 'DCF Analysis', to: `/stock/${lowerCaseTicker}/analysis` },
      { label: 'Reverse DCF', to: `/stock/${lowerCaseTicker}/reverse-dcf` },
      { label: 'Charts', to: `/stock/${lowerCaseTicker}/charts` },
      { label: 'Snapshot', to: `/stock/${lowerCaseTicker}/snapshot` },
      {
        label: 'Financials',
        to: `/stock/${lowerCaseTicker}/financials/balance-sheet`,
        activePrefix: `/stock/${lowerCaseTicker}/financials`,
      },
      { label: 'SEC Filings', to: `/stock/${lowerCaseTicker}/sec-filings` },
    ];
  }, [lowerCaseTicker]);

  const tabBg = useColorModeValue('white', 'gray.900');
  const tabBorder = useColorModeValue('gray.200', 'gray.700');
  const activeTabBg = useColorModeValue('blue.100', 'blue.900');
  const activeTabColor = useColorModeValue('blue.700', 'blue.200');
  const inactiveTabBg = useColorModeValue('gray.100', 'gray.800');
  const inactiveTabColor = useColorModeValue('gray.700', 'gray.200');

  if (isNotFound) {
    return <NotFound />;
  }

  return (
    <Box p={{ base: 3, md: 4 }}>
      {mobileTabs.length > 0 && (
        <Box
          display={{ base: 'flex', md: 'none' }}
          gap={2}
          mx={-3}
          mb={3}
          px={3}
          pb={3}
          overflowX="auto"
          borderBottom="1px solid"
          borderColor={tabBorder}
          bg={tabBg}
          position="sticky"
          top="var(--nav-h)"
          zIndex={20}
          css={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {mobileTabs.map((tab) => {
            const isActive = tab.exact
              ? location.pathname === tab.to
              : location.pathname.startsWith(tab.activePrefix || tab.to);

            return (
              <Box
                key={tab.to}
                as={Link}
                to={tab.to}
                flex="0 0 auto"
                px={4}
                py={2}
                borderRadius="md"
                bg={isActive ? activeTabBg : inactiveTabBg}
                color={isActive ? activeTabColor : inactiveTabColor}
                fontWeight={isActive ? 'semibold' : 'medium'}
                fontSize="sm"
                whiteSpace="nowrap"
                aria-current={isActive ? 'page' : undefined}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
      )}
      <StockReviewModeProvider ticker={normalizedTicker}>
        <StockReviewModeControl />
        <Outlet key={normalizedTicker} />  {/* Directly renders StockHome, StockAnalysis, StockFinancials */}
      </StockReviewModeProvider>
    </Box>
  );
}

export default StockPage;
